/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import PropTypes from 'prop-types';
import LazyImage from 'gatsby-core-theme/src/hooks/lazy-image';
import Link from 'gatsby-core-theme/src/hooks/link';
import { imagePrettyUrl, getAltText } from 'gatsby-core-theme/src/helpers/getters';
import StarRating from 'gatsby-core-theme/src/components/molecules/star-rating/one-star';
import ReviewLink from 'gatsby-core-theme/src/components/atoms/review-link';
import useTranslate from '~hooks/useTranslate/useTranslate';
// import Author from '../../atoms/author/template-one';
import styles from './operator-details.module.scss';
import PrettyLink from '~atoms/pretty-link';

const OperatorDetails = ({
  item,
  prettyLinkTracker = true,
  costumeStyle,
  template,
  ribbons,
  tracker = 'main',
  showRating = true,
  showAuthor = true,
  showReviewLink = true,
  showRibbon = false,
  contentRibbon = false,
  useOneStarRating = true,
  showRatingLabel = false,
  showRatingDecimal = false,
  ratingIcon,
  useTextRating = false,
  reviewNoFollow = false,
  imageWidth = 96,
  imageHeight = 96,
  imageLoad = 'lazy',
  showNonActiveRibbons = false,
  // isPPC,
  ribbonText = {
    inactive_ribbon: 'Inactive',
    coming_soon_ribbon: 'coming soon',
    not_recommended_ribbon: 'not recommended',
    blacklisted_ribbon: 'blacklisted',
  },
  moduleName = '',
  showRoundedRating = false,
  modulePosition,
  itemPosition,
}) => {
  const itemName = item?.name || item?.relation?.name;
  const status = item?.status || item?.relation?.status;
  const hasRibbons = typeof ribbons !== 'undefined' && ribbons.length > 0;
  const trackerType = tracker.toLowerCase().replace(' ', '_');

  const image = item?.logo;
  const reviewPath = item?.review_link ? item?.review_link : item?.path || null;
  const rating = item?.rating || item?.relation?.rating;
  const ribbonFinalText = useTranslate(`${status}_ribbon`, ribbonText[`${status}_ribbon`]);

  // eslint-disable-next-line react/prop-types
  const Tag = ({ children, classStyle = '', clickedElement = 'operator_logo' }) =>
    prettyLinkTracker ? (
      <PrettyLink
        operator={item?.relation || item}
        pageTemplate={template}
        moduleName={moduleName}
        tracker={trackerType}
        clickedElement={clickedElement}
        className={classStyle}
        modulePosition={modulePosition}
        itemPosition={itemPosition}
      >
        {children}
      </PrettyLink>
    ) : (
      <Link to={reviewPath} className={classStyle}>
        {children}
      </Link>
    );

  return (
    <div className={`${styles.operatorDetails} ${costumeStyle}`}>
      <Tag classStyle="Logo cta">
        <LazyImage
          src={imagePrettyUrl(image?.filename, imageWidth, imageHeight)}
          alt={getAltText(image, itemName)}
          width={imageWidth}
          height={imageHeight}
          className={styles.image}
          loading={imageLoad}
        />
        {showRibbon &&
          !contentRibbon &&
          (status === 'active'
            ? hasRibbons && (
                <span className={`${styles.ribbon} ${contentRibbon && styles.contentRibbon}`}>
                  {ribbons[0]}
                </span>
              )
            : showNonActiveRibbons && (
                <span
                  className={`${styles.ribbon} ${contentRibbon && styles.contentRibbon} ${
                    styles[status] || ''
                  }`}
                >
                  {ribbonFinalText}
                </span>
              ))}
      </Tag>

      <div className={styles.details}>
        {showRibbon &&
          contentRibbon &&
          (status === 'active'
            ? hasRibbons && (
                <span className={`${styles.ribbon} ${contentRibbon && styles.contentRibbon}`}>
                  {ribbons[0]}
                </span>
              )
            : showNonActiveRibbons && (
                <span
                  className={`${styles.ribbon} ${contentRibbon && styles.contentRibbon} ${
                    styles[status] || ''
                  }`}
                >
                  {ribbonFinalText}
                </span>
              ))}
        <Tag classStyle={`${styles.operatorName} name-cta `} clickedElement="operator_name">
          {itemName}
        </Tag>

        {(showAuthor || showRating) && (
          <div
            className={`${styles.ratingAuthor}
          ${(!useOneStarRating && styles.starRatings) || ''}
          ${(showRatingLabel && useOneStarRating && styles.ratinglabel) || ''}`}
          >
            {showRating && (
              <StarRating
                numOfStars={5}
                rating={rating}
                showLabel={showRatingLabel}
                showDecimal={showRatingDecimal}
                showRoundedRating={showRoundedRating}
                icon={ratingIcon}
                useText={useTextRating}
              />
            )}
          </div>
        )}
        {reviewPath && showReviewLink && (
          <ReviewLink
            className={`${styles?.reviewLink || ''} toplist-variant-one-gtm`}
            template={template}
            operatorName={itemName}
            reviewPath={reviewPath}
            nofollow={reviewNoFollow}
          />
        )}
      </div>
    </div>
  );
};

OperatorDetails.propTypes = {
  imageLoad: PropTypes.string,
  item: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    review_link: PropTypes.string,
    path: PropTypes.string,
    rating: PropTypes.string,
    status: PropTypes.string,
    relation: PropTypes.shape({
      name: PropTypes.string,
      rating: PropTypes.string,
      status: PropTypes.string,
    }),
    logo: PropTypes.shape({}),

    authorName: PropTypes.string,
    authorPath: PropTypes.string,
    authorImage: PropTypes.string,
    author: PropTypes.shape({
      name: PropTypes.string,
      profile_page_path: PropTypes.string,
      image: PropTypes.string,
    }),
  }),
  costumeStyle: PropTypes.string,
  reviewNoFollow: PropTypes.bool,
  prettyLinkTracker: PropTypes.bool,
  tracker: PropTypes.string,
  showRating: PropTypes.bool,
  showAuthor: PropTypes.bool,
  showReviewLink: PropTypes.bool,
  // showAuthorAsLink: PropTypes.bool,
  // authorTemplateTwo: PropTypes.bool,
  showRibbon: PropTypes.bool,
  template: PropTypes.string,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  ribbons: PropTypes.arrayOf(PropTypes.string),
  // icon: PropTypes.string,
  useOneStarRating: PropTypes.bool,
  showRatingLabel: PropTypes.bool,
  showRatingDecimal: PropTypes.bool,
  contentRibbon: PropTypes.bool,
  ribbonText: PropTypes.shape({}),
  showNonActiveRibbons: PropTypes.bool,
  // isPPC: PropTypes.bool,
  moduleName: PropTypes.string,
  showRoundedRating: PropTypes.bool,
  ratingIcon: PropTypes.shape({}),
  useTextRating: PropTypes.bool,
  modulePosition: PropTypes.number,
  itemPosition: PropTypes.number,
};

export default OperatorDetails;
